import { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

const differentials = [
  {
    icon: 'ri-user-star-line',
    title: 'Atendimento Especializado',
    description:
      'Cada cliente recebe atenção dedicada de um advogado especialista, sem terceirizações ou atendimentos genéricos.',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Atuação Estratégica',
    description:
      'Traçamos a melhor rota jurídica para cada caso, combinando técnica jurídica com visão estratégica de negócios.',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Segurança Jurídica',
    description:
      'Processos conduzidos com máximo rigor técnico para garantir a plena segurança do seu patrimônio imobiliário.',
  },
  {
    icon: 'ri-medal-line',
    title: 'Experiência Comprovada',
    description:
      'Vasta experiência em processos imobiliários complexos, com histórico sólido de resultados efetivos para clientes.',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Agilidade e Precisão',
    description:
      'Combinamos velocidade de execução com precisão técnica para entregar resultados no menor prazo possível.',
  },
];

export default function Differentials() {
  const { ref: headRef, inView: headIn } = useInView();

  return (
    <section id="diferenciais" className="w-full py-24 md:py-32 relative overflow-hidden" style={{ background: '#F5F5F5' }}>
      {/* Decorative bg element */}
      <div
        className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-5"
        style={{ background: '#0A2A52', transform: 'translate(30%, -30%)' }}
      ></div>
      <div
        className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-5"
        style={{ background: '#D4AF37', transform: 'translate(-30%, 30%)' }}
      ></div>

      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          ref={headRef}
          className={`text-center mb-16 transition-all duration-1000 ${headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-16" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37)' }}></div>
            <span className="font-sans text-xs tracking-[3px] uppercase font-medium" style={{ color: '#D4AF37' }}>
              Por Que Nos Escolher
            </span>
            <div className="h-px w-16" style={{ background: 'linear-gradient(90deg, #D4AF37, transparent)' }}></div>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl font-bold" style={{ color: '#0A2A52' }}>
            Nossos <span style={{ color: '#C29B2E' }}>Diferenciais</span>
          </h2>
          <p className="font-sans text-sm md:text-base font-light mt-4 max-w-2xl mx-auto" style={{ color: '#123B73' }}>
            O que nos torna a escolha certa para proteger e valorizar seu patrimônio imobiliário.
          </p>
        </div>

        {/* Differentials grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {differentials.map((item, i) => (
            <DifferentialCard key={i} item={item} index={i} />
          ))}
          {/* Last card – CTA card */}
          <CTACard />
        </div>
      </div>
    </section>
  );
}

function DifferentialCard({ item, index }: { item: typeof differentials[0]; index: number }) {
  const { ref, inView } = useInView();
  const [open, setOpen] = useState(false);

  return (
    <div
      ref={ref}
      className={`rounded-sm transition-all duration-700 group ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{
        background: 'white',
        border: '1px solid #E8E8E8',
        transitionDelay: `${index * 100}ms`,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = '#D4AF37';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = '#E8E8E8';
      }}
    >
      {/* ── MOBILE LAYOUT ── */}
      <div className="md:hidden">
        {/* Header row — always visible, clickable */}
        <button
          className="w-full flex items-center gap-3 p-5 cursor-pointer text-left"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <div
            className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-sm transition-all duration-300"
            style={{ background: '#0A2A52' }}
          >
            <i className={`${item.icon} text-xl`} style={{ color: '#D4AF37' }}></i>
          </div>
          <h3 className="font-serif text-base font-semibold flex-1 text-left" style={{ color: '#0A2A52' }}>
            {item.title}
          </h3>
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i
              className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
              style={{ color: '#D4AF37' }}
            ></i>
          </div>
        </button>

        {/* Description — visible only when open */}
        <div
          className="overflow-hidden transition-all duration-300"
          style={{ maxHeight: open ? '200px' : '0px', opacity: open ? 1 : 0 }}
        >
          <p className="font-sans text-sm leading-relaxed px-5 pb-5" style={{ color: '#123B73' }}>
            {item.description}
          </p>
        </div>
      </div>

      {/* ── DESKTOP LAYOUT ── original, full card */}
      <div className="hidden md:block p-7">
        <div
          className="w-14 h-14 flex items-center justify-center rounded-sm mb-5 transition-all duration-300 group-hover:scale-105"
          style={{ background: '#0A2A52' }}
        >
          <i className={`${item.icon} text-2xl`} style={{ color: '#D4AF37' }}></i>
        </div>
        <h3 className="font-serif text-lg font-semibold mb-3" style={{ color: '#0A2A52' }}>
          {item.title}
        </h3>
        <p className="font-sans text-sm leading-relaxed" style={{ color: '#123B73' }}>
          {item.description}
        </p>
      </div>
    </div>
  );
}

function CTACard() {
  const { ref, inView } = useInView();
  const WA_LINK = 'https://wa.me/5511984333601?text=Ol%C3%A1%2C%20gostaria%20de%20uma%20consulta%20sobre%20direito%20imobili%C3%A1rio.';

  return (
    <div
      ref={ref}
      className={`p-7 rounded-sm flex flex-col justify-center items-start transition-all duration-700 delay-500 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ background: '#0A2A52' }}
    >
      <div className="h-px w-12 mb-6" style={{ background: '#D4AF37' }}></div>
      <h3 className="font-serif text-xl font-bold mb-3 leading-snug" style={{ color: '#F5F5F5' }}>
        Pronto para regularizar seu imóvel?
      </h3>
      <p className="font-sans text-sm leading-relaxed mb-6" style={{ color: '#D9D9D9' }}>
        Fale com nosso especialista e receba uma análise personalizada do seu caso.
      </p>
      <a
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-6 py-3 font-sans text-xs font-semibold tracking-wider uppercase whitespace-nowrap cursor-pointer transition-all duration-300 rounded-sm"
        style={{ background: '#D4AF37', color: '#0A2A52' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = '#C29B2E'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = '#D4AF37'; }}
      >
        <i className="ri-whatsapp-line"></i>
        Falar Agora
      </a>
    </div>
  );
}
